/**
 * WebGPUPipeline - WebGPU render pipeline with TSL-based post-processing.
 * Manages effect nodes, MRT configuration, and HUD compositing.
 */

import * as THREE from 'three';
import { blendColor, diffuseColor, directionToColor, emissive, metalness, mrt, normalView, output, pass, roughness, vec2, vec4, velocity } from 'three/tsl';
import { MeshBasicNodeMaterial, RendererUtils, RenderPipeline } from 'three/webgpu';

import { Delegate } from '../../../utils/Delegate.js';
import { IPostProcessPipeline, type PipelineHooks } from '../IPostProcessPipeline.js';
import { DefaultPostprocessEffectConfigs, PostProcessPass } from '../PostProcessConfig.js';
import { ExclusionMaskRenderer } from '../utils/ExclusionMaskRenderer.js';
import { SceneDepthRenderer } from '../utils/SceneDepthRenderer.js';

import type { WebGPURenderer } from '../../Renderer.js';
import type { ToneMappingEffect } from '../effects/ToneMappingEffect.js';
import type { IPostProcessEffect } from '../IPostProcessEffect.js';
import type { BaseEffectConfig, EffectContext, WebGPUComposeContext, WebGPUEffectNode } from '../IPostProcessEffect.js';
import type { BloomEffectConfig, ToneMappingEffectConfig } from '../PostProcessConfig.js';

const EXCLUSIVE_EXCLUDED_LAYER_USERDATA_KEY = 'excludePostprocessExclusiveLayer';

const DEBUG = {
  logCameraLayerTransitions: false,
  assertViewportAfterExclusionMask: false,
} as const;

type RendererState = Parameters<typeof RendererUtils.resetRendererState>[1];

/**
 * WebGPU render pipeline (including postprocessing).
 */
export class WebGPUPipeline extends IPostProcessPipeline {
  override readonly rendererType = 'webgpu' as const;
  override readonly hooks: PipelineHooks;

  protected renderer: WebGPURenderer;
  protected renderPipeline: RenderPipeline | null = null;
  protected enabled = true;
  protected initialized = false;
  protected disposed = false;

  // Effect management
  protected effects = new Map<string, IPostProcessEffect>();
  protected effectConfigs = new Map<string, BaseEffectConfig>();
  protected effectNodes = new Map<string, WebGPUEffectNode>();

  // Note: Cached nodes are now stored generically in effectNodes map

  // Scene/camera tracking
  protected scene: THREE.Scene | null = null;
  protected camera: THREE.Camera | null = null;
  // Store original camera layers before filtering for scene pass creation
  protected originalCameraLayersForScenePass: number = 0xFFFFFFFF;

  // HUD scene/camera for compositing on top of post-processed output
  protected hudScene: THREE.Scene | null = null;
  protected hudCamera: THREE.Camera | null = null;

  // Hidden/excluded objects
  protected hiddenObjects: Set<THREE.Object3D> = new Set();
  protected excludedObjects: Set<THREE.Object3D> = new Set();
  protected excludedLayer = 30;
  protected excludedLayerSyncDirty = false;
  protected exclusionMaskRenderer = new ExclusionMaskRenderer();
  protected sceneDepthRenderer = new SceneDepthRenderer();
  protected sceneDepthMaterial: MeshBasicNodeMaterial | null = null;
  protected bloomSourceLayers: THREE.Layers | null = null;
  protected scenePassLayers: THREE.Layers | null = null;
  protected exclusiveExcludedOriginalLayerMasks = new WeakMap<THREE.Object3D, number>();
  protected lastWindowSize: { width: number; height: number } | null = null;
  protected hasLoggedRenderPipelineFailure = false;

  // Rebuild tracking
  protected needsRebuild = true;

  // Cached arrays/objects to avoid per-frame allocations
  protected hiddenToRestore: THREE.Object3D[] = [];
  private drawingBufferSizeCache = new THREE.Vector2();
  private rendererStateCache = {} as RendererState;
  protected preRenderContext: EffectContext & {
    hiddenObjects: ReadonlySet<THREE.Object3D>;
    excludedObjects: ReadonlySet<THREE.Object3D>;
    sharedSceneDepthTexture?: THREE.Texture | null;
    sharedExclusionMaskTexture?: THREE.Texture | null;
    sharedHasExclusions?: boolean;
  } = {
      scene: null as unknown as THREE.Scene,
      camera: null as unknown as THREE.Camera,
      renderer: null as unknown as WebGPURenderer,
      hiddenObjects: new Set(),
      excludedObjects: new Set(),
      excludedLayer: 30,
      sharedSceneDepthTexture: undefined,
      sharedExclusionMaskTexture: undefined,
      sharedHasExclusions: false,
    };

  constructor(renderer: WebGPURenderer) {
    super();
    this.renderer = renderer;

    this.hooks = {
      onBeforeRender: new Delegate(),
      onAfterRender: new Delegate(),
      onEffectCreated: new Delegate(),
      onEffectDestroyed: new Delegate(),
      onPipelineRebuild: new Delegate(),
    };
  }

  async initialize(): Promise<void> {
    if (this.disposed) return;
    if (this.initialized) return;

    try {
      this.initialized = true;
      const backend = this.renderer.coordinateSystem === THREE.WebGPUCoordinateSystem ? 'WebGPU' : 'WebGL';
      console.log(`[WebGPUPipeline] Initialized with ${backend} backend.`);
    } catch (error) {
      console.warn('[WebGPUPipeline] Initialization failed:', error);
    }
  }

  // ============= Effect Registration =============

  public override registerEffect(effect: IPostProcessEffect): void {
    if (this.effects.has(effect.type)) {
      console.warn(`[WebGPUPipeline] Effect "${effect.type}" already registered. Overwriting.`);
      this.unregisterEffect(effect.type);
    }

    this.effects.set(effect.type, effect);
    const defaultConfig = effect.getDefaultConfig();
    this.effectConfigs.set(effect.type, defaultConfig);
    this.needsRebuild = true;

    this.hooks.onEffectCreated.invoke(effect, defaultConfig);
  }

  public override unregisterEffect(type: string): void {
    const effect = this.effects.get(type);
    if (!effect) return;

    this.effectNodes.delete(type);
    this.effects.delete(type);
    this.effectConfigs.delete(type);
    this.needsRebuild = true;

    this.hooks.onEffectDestroyed.invoke(effect);
  }

  public override getEffect(type: string): IPostProcessEffect | null {
    return this.effects.get(type) ?? null;
  }

  public override getAllEffects(): ReadonlyArray<IPostProcessEffect> {
    return Array.from(this.effects.values());
  }

  // ============= Configuration =============

  public override configureEffect(type: string, config: Partial<BaseEffectConfig> & Record<string, unknown>): void {
    const currentConfig = this.effectConfigs.get(type);
    if (!currentConfig) {
      console.warn(`[WebGPUPipeline] Effect "${type}" not found.`);
      return;
    }

    const oldConfig = { ...currentConfig };
    const updatedConfig = { ...currentConfig, ...config };
    this.effectConfigs.set(type, updatedConfig);

    // Apply live uniform updates where possible
    this.applyLiveConfigUpdate(type, updatedConfig);

    // Check if rebuild is required
    const effect = this.effects.get(type);
    if (effect?.requiresRebuild?.(oldConfig, updatedConfig)) {
      this.needsRebuild = true;
    }
  }

  public override getEffectConfig(type: string): BaseEffectConfig | null {
    return this.effectConfigs.get(type) ?? null;
  }

  public override setEffectEnabled(type: string, enabled: boolean): void {
    const config = this.effectConfigs.get(type);
    if (config) {
      config.enabled = enabled;
      this.needsRebuild = true;
    }
  }

  // ============= Rendering =============

  @THREE.profile
  public override render(scene: THREE.Scene, camera: THREE.Camera, windowSize: { width: number; height: number }): void {
    if (this.disposed) {
      return;
    }

    this.setSize(windowSize.width, windowSize.height);

    if (!this.initialized) {
      this.renderer.render(scene, camera);
      return;
    }

    // Sync excluded layer on all descendants — children may have been added after
    // addExcludedObjects (e.g., async GLTF load), so their layers wouldn't have been set.
    THREE.ProfilerService.begin('WebGPUPipeline.render.syncExcludedLayer');
    const hasExcludedObjects = this.excludedObjects.size > 0;
    if (hasExcludedObjects && this.excludedLayerSyncDirty) {
      for (const obj of this.excludedObjects) {
        obj.traverse(child => {
          child.layers.enable(this.excludedLayer);
        });
      }
      this.excludedLayerSyncDirty = false;
    }

    // Store original camera layers for scene pass (need full layers for depth).
    // Include excluded layer so "excluded-layer-only" editor meshes (e.g., viewport grid)
    // still render in the main scene pass.
    const excludedLayerMask = 1 << this.excludedLayer;
    this.originalCameraLayersForScenePass = hasExcludedObjects
      ? (camera.layers.mask | excludedLayerMask)
      : camera.layers.mask;
    if (this.bloomSourceLayers) {
      this.bloomSourceLayers.mask = this.originalCameraLayersForScenePass & ~excludedLayerMask;
    }
    THREE.ProfilerService.end('WebGPUPipeline.render.syncExcludedLayer');

    // Never mutate the shared camera layers during render; pass-layer filtering
    // should happen via PassNode.setLayers so controls/picking see stable state.
    const cameraLayersBeforeRender = camera.layers.mask;

    try {
      // Rebuild pipeline if needed
      if (this.needsRebuild || this.scene?.uuid !== scene.uuid || this.camera?.uuid !== camera.uuid) {
        this.scene = scene;
        this.camera = camera;
        this.buildPipeline();
      }

      if (this.shouldUseDirectRendererFastPath()) {
        THREE.ProfilerService.begin('WebGPUPipeline.render.shouldUseDirectRendererFastPath');
        this.hooks.onBeforeRender.invoke(scene, camera);
        this.applyRendererToneMapping();
        this.renderer.render(scene, camera);
        this.hasLoggedRenderPipelineFailure = false;
        this.hooks.onAfterRender.invoke(scene, camera);
        THREE.ProfilerService.end('WebGPUPipeline.render.shouldUseDirectRendererFastPath');
        return;
      }

      this.hooks.onBeforeRender.invoke(scene, camera);

      // Handle hidden objects - reuse array, clear with length = 0
      this.hiddenToRestore.length = 0;
      for (const obj of this.hiddenObjects) {
        if (obj.visible) {
          obj.visible = false;
          this.hiddenToRestore.push(obj);
        }
      }

      // Call preRender on all enabled effects that implement it - reuse context object
      this.preRenderContext.scene = scene;
      this.preRenderContext.camera = camera;
      this.preRenderContext.renderer = this.renderer;
      this.preRenderContext.hiddenObjects = this.hiddenObjects;
      this.preRenderContext.excludedObjects = this.excludedObjects;
      this.preRenderContext.excludedLayer = this.excludedLayer;
      this.preRenderContext.sharedSceneDepthTexture = undefined;
      this.preRenderContext.sharedExclusionMaskTexture = undefined;
      this.preRenderContext.sharedHasExclusions = false;

      const toneMappingEffect = this.effects.get(PostProcessPass.ToneMapping) as ToneMappingEffect | undefined;
      const toneMappingConfig = this.effectConfigs.get(PostProcessPass.ToneMapping) as ToneMappingEffectConfig | undefined;
      const skipNonToneMapped = toneMappingConfig?.skipNonToneMappedMaterials ?? false;

      if (toneMappingEffect && skipNonToneMapped) {
        toneMappingEffect.syncSkipNonToneMappedMaterials(scene, this.excludedLayer);
      }

      const shouldRenderExclusionMask = hasExcludedObjects || skipNonToneMapped;
      const objectOutlineEffect = this.effects.get(PostProcessPass.ObjectOutline) as (IPostProcessEffect & {
        hasWebGPUSelection?: () => boolean;
      }) | undefined;
      const objectOutlineConfig = this.effectConfigs.get(PostProcessPass.ObjectOutline);
      const shouldRenderOutlineDepth = objectOutlineConfig?.enabled === true && objectOutlineEffect?.hasWebGPUSelection?.() === true;
      const shouldRenderSharedDepth = shouldRenderExclusionMask || shouldRenderOutlineDepth;

      if (shouldRenderSharedDepth) {
        this.renderSharedSceneDepth(scene, camera, this.originalCameraLayersForScenePass);

        if (shouldRenderExclusionMask) {
          this.exclusionMaskRenderer.renderMask({
            renderer: this.renderer,
            scene,
            camera,
            excludedLayer: this.excludedLayer,
          });
        } else {
          this.exclusionMaskRenderer.clearHasExclusions();
        }

        if (DEBUG.assertViewportAfterExclusionMask) {
          const canvasSize = this.renderer.getSize(new THREE.Vector2());
          const viewport = new THREE.Vector4();
          this.renderer.getViewport(viewport);
          console.assert(
            this.renderer.getRenderTarget() === null
              && viewport.x === 0
              && viewport.y === 0
              && viewport.z === canvasSize.width
              && viewport.w === canvasSize.height,
            '[WebGPUPipeline] exclusion mask pass left stale render target or viewport',
            { renderTarget: this.renderer.getRenderTarget(), viewport, canvasSize }
          );
        }
        this.preRenderContext.sharedSceneDepthTexture = this.exclusionMaskRenderer.getSceneDepthTexture();
        this.preRenderContext.sharedExclusionMaskTexture = shouldRenderExclusionMask
          ? this.exclusionMaskRenderer.getExclusionMaskTexture()
          : undefined;
        this.preRenderContext.sharedHasExclusions = hasExcludedObjects;
      } else {
        this.exclusionMaskRenderer.clearHasExclusions();
      }

      THREE.ProfilerService.begin('WebGPUPipeline.render.preRender');
      for (const [type, effect] of this.effects) {
        const config = this.effectConfigs.get(type);
        if (config?.enabled && effect.preRender) {
          effect.preRender(this.preRenderContext);
        }
      }
      THREE.ProfilerService.end('WebGPUPipeline.render.preRender');

      if (!this.enabled || !this.renderPipeline) {
        THREE.ProfilerService.begin('WebGPUPipeline.render.applyRendererToneMapping');
        // Apply tone mapping settings from ToneMappingEffect config when post-processing is disabled
        this.applyRendererToneMapping();
        this.renderer.render(scene, camera);
        this.hasLoggedRenderPipelineFailure = false;
        THREE.ProfilerService.end('WebGPUPipeline.render.applyRendererToneMapping');
      } else {
        THREE.ProfilerService.begin('WebGPUPipeline.render.renderPipeline');
        // Keep scene pass layers synced without mutating the shared camera object.
        if (this.scenePassLayers) {
          this.scenePassLayers.mask = this.originalCameraLayersForScenePass;
        }
        try {
          this.renderPipeline.render();
          this.hasLoggedRenderPipelineFailure = false;
        } catch (error) {
          this.needsRebuild = true;
          if (!this.hasLoggedRenderPipelineFailure) {
            console.error('[WebGPUPipeline] RenderPipeline render failed; falling back to direct renderer.', {
              width: windowSize.width,
              height: windowSize.height,
              error,
            });
            this.hasLoggedRenderPipelineFailure = true;
          }
          this.applyRendererToneMapping();
          this.renderer.render(scene, camera);
        }
        THREE.ProfilerService.end('WebGPUPipeline.render.renderPipeline');
      }

      this.hooks.onAfterRender.invoke(scene, camera);
    } finally {
      // Restore hidden objects
      for (const obj of this.hiddenToRestore) obj.visible = true;

      if (DEBUG.logCameraLayerTransitions && camera.layers.mask !== cameraLayersBeforeRender) {
        console.warn('[WebGPUPipeline] camera.layers.mask differed before vs after render()', {
          before: cameraLayersBeforeRender,
          after: camera.layers.mask,
        });
      }
    }
  }

  private ensureSceneDepthMaterial(): MeshBasicNodeMaterial {
    if (!this.sceneDepthMaterial) {
      this.sceneDepthMaterial = new MeshBasicNodeMaterial({ color: 0x000000 });
      this.sceneDepthMaterial.depthTest = true;
      this.sceneDepthMaterial.depthWrite = true;
      this.sceneDepthMaterial.colorWrite = false;
      this.sceneDepthMaterial.fog = false;
      this.sceneDepthMaterial.toneMapped = false;
    }

    return this.sceneDepthMaterial;
  }

  private renderSharedSceneDepth(scene: THREE.Scene, camera: THREE.Camera, sceneDepthLayerMask: number): void {
    this.exclusionMaskRenderer.updateMaskTarget(this.renderer);

    const target = this.exclusionMaskRenderer.getMaskRenderTarget();
    if (!target) return;

    const originalOverride = scene.overrideMaterial;
    const originalBackground = scene.background;
    const originalEnvironment = scene.environment;
    const originalAutoClear = this.renderer.autoClear;
    const shadowMapEnabled = this.renderer.shadowMap.enabled;
    let rendererState: RendererState | undefined;

    try {
      this.renderer.shadowMap.enabled = false;
      rendererState = RendererUtils.resetRendererState(this.renderer, this.rendererStateCache);

      scene.background = null;
      scene.environment = null;
      this.renderer.autoClear = false;

      this.renderer.setRenderTarget(target);
      this.renderer.setViewport(0, 0, target.width, target.height);
      this.renderer.setScissorTest(false);
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.clearColor();
      this.renderer.clearDepth();

      this.sceneDepthRenderer.render({
        scene,
        camera,
        sceneDepthLayerMask,
        depthMaterial: this.ensureSceneDepthMaterial(),
        renderScene: (profileName, overrideMaterial) => {
          THREE.ProfilerService.begin(profileName);
          try {
            scene.overrideMaterial = overrideMaterial;
            this.renderer.render(scene, camera);
          } finally {
            THREE.ProfilerService.end(profileName);
            scene.overrideMaterial = null;
          }
        },
      });
    } finally {
      scene.overrideMaterial = originalOverride;
      scene.background = originalBackground;
      scene.environment = originalEnvironment;
      this.renderer.autoClear = originalAutoClear;
      this.renderer.shadowMap.enabled = shadowMapEnabled;

      if (rendererState) {
        RendererUtils.restoreRendererState(this.renderer, rendererState);
        this.restoreRendererViewport();
      }
    }
  }

  /** Restore the full logical canvas viewport after an offscreen shared-depth pass. */
  private restoreRendererViewport(): void {
    this.renderer.getSize(this.drawingBufferSizeCache);
    this.renderer.setViewport(0, 0, this.drawingBufferSizeCache.width, this.drawingBufferSizeCache.height);
  }

  public override setSize(width: number, height: number): void {
    if (this.disposed) {
      return;
    }
    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return;
    }

    const nextWidth = Math.max(1, Math.floor(width));
    const nextHeight = Math.max(1, Math.floor(height));
    const sizeUnchanged = this.lastWindowSize?.width === nextWidth && this.lastWindowSize?.height === nextHeight;
    if (sizeUnchanged) {
      return;
    }

    this.lastWindowSize = { width: nextWidth, height: nextHeight };
    this.needsRebuild = true;
  }

  public override setEnabled(enabled: boolean): void {
    if (this.disposed) {
      return;
    }
    const wasEnabled = this.enabled;
    this.enabled = enabled;
    // Re-enable can otherwise reuse a stale pipeline while renderer tone mapping
    // remains configured for the direct-render fallback path.
    if (!wasEnabled && enabled) {
      this.needsRebuild = true;
    }
  }

  public override isEnabled(): boolean {
    return this.enabled && this.initialized;
  }

  public override dispose(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;

    this.renderPipeline?.dispose();
    this.renderPipeline = null;

    for (const effect of this.effects.values()) {
      effect.dispose?.();
    }

    this.effects.clear();
    this.effectConfigs.clear();
    this.effectNodes.clear();
    this.hiddenObjects.clear();
    this.excludedObjects.clear();
    this.exclusionMaskRenderer.dispose();
    this.exclusionMaskRenderer = new ExclusionMaskRenderer();
    this.sceneDepthRenderer.dispose();
    this.sceneDepthRenderer = new SceneDepthRenderer();
    this.sceneDepthMaterial?.dispose();
    this.sceneDepthMaterial = null;
    this.scene = null;
    this.camera = null;
    this.hudScene = null;
    this.hudCamera = null;
    this.lastWindowSize = null;
    this.initialized = false;
  }

  // ============= Hidden/Excluded Objects =============

  public override addHiddenObjects(...objects: THREE.Object3D[]): void {
    for (const obj of objects) this.hiddenObjects.add(obj);
  }

  public override removeHiddenObjects(...objects: THREE.Object3D[]): void {
    for (const obj of objects) this.hiddenObjects.delete(obj);
  }

  public override clearHiddenObjects(): void {
    this.hiddenObjects.clear();
  }

  public override addExcludedObjects(...objects: THREE.Object3D[]): void {
    let changed = false;
    for (const obj of objects) {
      if (this.excludedObjects.has(obj)) continue;
      this.excludedObjects.add(obj);
      changed = true;
      obj.traverse(child => {
        const useExclusiveExcludedLayer = child.userData[EXCLUSIVE_EXCLUDED_LAYER_USERDATA_KEY] === true;
        if (useExclusiveExcludedLayer) {
          if (!this.exclusiveExcludedOriginalLayerMasks.has(child)) {
            this.exclusiveExcludedOriginalLayerMasks.set(child, child.layers.mask);
          }
          child.layers.disableAll();
          child.layers.enable(this.excludedLayer);
        } else {
          // Keep original layers so excluded objects still render in scene pass.
          child.layers.enable(this.excludedLayer);
        }
        // Mark as manually excluded to prevent other systems from overriding
        child.userData.excludePostprocess = true;
      });
    }
    if (changed) {
      this.excludedLayerSyncDirty = true;
    }
  }

  public override removeExcludedObjects(...objects: THREE.Object3D[]): void {
    let changed = false;
    for (const obj of objects) {
      if (!this.excludedObjects.has(obj)) continue;
      this.excludedObjects.delete(obj);
      changed = true;
      obj.traverse(child => {
        const originalLayerMask = this.exclusiveExcludedOriginalLayerMasks.get(child);
        if (originalLayerMask !== undefined) {
          child.layers.mask = originalLayerMask;
          this.exclusiveExcludedOriginalLayerMasks.delete(child);
        } else {
          child.layers.disable(this.excludedLayer);
        }
        delete child.userData.excludePostprocess;
      });
    }
    if (changed) {
      this.excludedLayerSyncDirty = this.excludedObjects.size > 0;
    }
  }

  public override clearExcludedObjects(): void {
    for (const obj of this.excludedObjects) {
      obj.traverse(child => {
        const originalLayerMask = this.exclusiveExcludedOriginalLayerMasks.get(child);
        if (originalLayerMask !== undefined) {
          child.layers.mask = originalLayerMask;
          this.exclusiveExcludedOriginalLayerMasks.delete(child);
        } else {
          child.layers.disable(this.excludedLayer);
        }
        delete child.userData.excludePostprocess;
      });
    }
    this.excludedObjects.clear();
    this.excludedLayerSyncDirty = false;
  }

  public override setExcludedLayer(layer: number): void {
    const nextLayer = Math.max(0, Math.min(31, layer));
    if (nextLayer === this.excludedLayer) return;

    const previousLayer = this.excludedLayer;
    this.excludedLayer = nextLayer;

    for (const obj of this.excludedObjects) {
      obj.traverse(child => {
        child.layers.disable(previousLayer);
        child.layers.enable(nextLayer);
      });
    }
    this.excludedLayerSyncDirty = this.excludedObjects.size > 0;
  }

  public override getExcludedLayer(): number {
    return this.excludedLayer;
  }

  public override getExcludedObjects(): ReadonlySet<THREE.Object3D> {
    return this.excludedObjects;
  }

  /** Mark excluded layer state dirty after dynamic child hierarchy changes. */
  public markExcludedLayerSyncDirty(): void {
    this.excludedLayerSyncDirty = this.excludedObjects.size > 0;
  }

  // ============= HUD Compositing =============

  /**
   * Set the HUD scene and camera for compositing on top of post-processed output.
   * The HUD is rendered as a separate pass and blended on top after all effects.
   * Set both to null to disable HUD compositing.
   */
  public setHud(scene: THREE.Scene | null, camera: THREE.Camera | null): void {
    if (this.hudScene !== scene || this.hudCamera !== camera) {
      this.hudScene = scene;
      this.hudCamera = camera;
      this.needsRebuild = true;
    }
  }

  /** Return the current HUD scene (used by PostProcessManager for clean-frame rendering). */
  public getHudScene(): THREE.Scene | null {
    return this.hudScene;
  }

  // ============= Private: Pipeline Building =============

  /** Apply live uniform updates without rebuilding the pipeline */
  protected applyLiveConfigUpdate(type: string, config: BaseEffectConfig): void {
    const effect = this.effects.get(type);
    if (!effect) return;

    // Apply renderer-level config changes (e.g., tone mapping)
    if (effect.applyRendererConfig) {
      effect.applyRendererConfig(config, this.renderer);
    }

    // Apply effect-specific config updates
    const node = this.effectNodes.get(type);
    if (effect.applyWebGPUConfig && node) {
      effect.applyWebGPUConfig(node, config);
    }
  }

  /** Apply tone mapping settings to renderer when post-processing is disabled */
  protected applyRendererToneMapping(): void {
    // Get tone mapping effect config (enabled or not)
    const toneMappingConfig = this.effectConfigs.get(PostProcessPass.ToneMapping) as ToneMappingEffectConfig | undefined;

    if (toneMappingConfig) {
      // Apply tone mapping settings from the config, but enable native renderer tone mapping
      const defaults = DefaultPostprocessEffectConfigs[PostProcessPass.ToneMapping];
      this.renderer.toneMapping = toneMappingConfig.mode ?? defaults.mode ?? THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = toneMappingConfig.exposure ?? defaults.exposure ?? 1.0;
    } else {
      // Fallback to default tone mapping if no config available
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.0;
    }
  }

  @THREE.profile
  protected buildPipeline(): void {
    if (this.disposed) return;
    if (!this.scene || !this.camera) return;

    this.renderPipeline?.dispose();
    this.renderPipeline = null;
    this.effectNodes.clear();

    // Get all enabled effects sorted by order (single pass: iterate, collect, sort in-place)
    const enabledEffects: IPostProcessEffect[] = [];
    for (const [type, effect] of this.effects) {
      const config = this.effectConfigs.get(type);
      if (config?.enabled) enabledEffects.push(effect);
    }
    enabledEffects.sort((a, b) => {
      const aConfig = this.effectConfigs.get(a.type);
      const bConfig = this.effectConfigs.get(b.type);
      const aOrder = aConfig?.order ?? a.order;
      const bOrder = bConfig?.order ?? b.order;
      return aOrder - bOrder;
    });

    // If no effects enabled, skip pipeline creation
    if (enabledEffects.length === 0) {
      this.needsRebuild = false;
      return;
    }

    // Apply renderer-level config for all effects
    for (const effect of enabledEffects) {
      const config = this.effectConfigs.get(effect.type);
      if (config && effect.applyRendererConfig) {
        effect.applyRendererConfig(config, this.renderer);
      }
    }

    this.exclusionMaskRenderer.ensureMaskMaterials();
    this.exclusionMaskRenderer.updateMaskTarget(this.renderer);
    const exclusionMaskTexture = this.exclusionMaskRenderer.getExclusionMaskTexture();
    const sharedSceneDepthTexture = this.exclusionMaskRenderer.getSceneDepthTexture();

    // Create RenderPipeline
    this.renderPipeline = new RenderPipeline(this.renderer);
    this.renderPipeline.outputColorTransform = true;

    // Gather MRT requirements from all enabled effects
    const mrtOutputs = new Set<string>(['output']); // Always need output
    for (const effect of enabledEffects) {
      const config = this.effectConfigs.get(effect.type);
      if (!config || !effect.createWebGPUNode) continue;

      const context: EffectContext = {
        scene: this.scene,
        camera: this.camera,
        renderer: this.renderer,
        excludedObjects: this.excludedObjects,
        excludedLayer: this.excludedLayer,
        exclusionMaskTexture,
        sharedSceneDepthTexture,
      };

      // Create or get cached node
      let effectNode = this.effectNodes.get(effect.type);
      if (!effectNode) {
        effectNode = effect.createWebGPUNode(config, context);
        if (!effectNode) continue;
        this.effectNodes.set(effect.type, effectNode);
      }

      // Collect MRT outputs
      if (effectNode.needsMRT && effectNode.mrtOutputs) {
        for (const output of effectNode.mrtOutputs) {
          mrtOutputs.add(output);
        }
      }
    }

    // Check if any effect needs a pre-pass
    const needsPrePass = enabledEffects.some(e => e.needsPrePass?.());
    let prePass: any = null;

    if (needsPrePass) {
      // Create shared pre-pass (first effect that needs it creates it)
      for (const effect of enabledEffects) {
        if (effect.needsPrePass?.()) {
          const config = this.effectConfigs.get(effect.type);
          if (config) {
            prePass = effect.createPrePass?.(this.scene, this.camera, config);
            break; // Use first pre-pass
          }
        }
      }
    }

    // Keep scene pass layer filtering on the PassNode instead of mutating camera layers.
    this.scenePassLayers ??= new THREE.Layers();
    this.scenePassLayers.mask = this.originalCameraLayersForScenePass;
    const scenePass = pass(this.scene, this.camera);
    scenePass.setLayers(this.scenePassLayers);

    const mrtConfig: Record<string, any> = { output: output };

    const mrtNodeFactories: Record<string, () => any> = {
      emissive: () => emissive,
      // alpha=1 marks this as an opaque geometry pixel; transparent materials (particles)
      // write alpha=0 via material.mrtNode so AOEffect can build a masked depth node that
      // makes GTAO skip transparent pixels entirely.
      normal: () => vec4(directionToColor(normalView), 1),
      velocity: () => velocity,
      diffuseColor: () => diffuseColor,
      metalrough: () => vec2(metalness, roughness),
    };

    for (const mrtOutputName of mrtOutputs) {
      if (mrtOutputName === 'output') continue;
      const createNode = mrtNodeFactories[mrtOutputName];
      if (createNode) {
        mrtConfig[mrtOutputName] = createNode();
      }
    }

    scenePass.setMRT(mrt(mrtConfig));

    // Build compose context
    let outputNode: any = scenePass.getTextureNode('output');
    const depthNode = scenePass.getTextureNode('depth');

    let bloomInputNode: any = null;
    let bloomDepthNode: any = null;

    const bloomConfig = this.effectConfigs.get(PostProcessPass.Bloom) as BloomEffectConfig | undefined;
    const bloomEnabled = bloomConfig?.enabled === true;
    const bloomSelectiveEmissive = bloomConfig?.selectiveEmissive ?? false;
    const useBloomSourcePass = bloomEnabled && !bloomSelectiveEmissive;

    if (useBloomSourcePass) {
      this.bloomSourceLayers ??= new THREE.Layers();
      const excludedLayerMask = 1 << this.excludedLayer;
      this.bloomSourceLayers.mask = this.originalCameraLayersForScenePass & ~excludedLayerMask;

      const bloomSourcePass = pass(this.scene, this.camera);
      bloomSourcePass.setLayers(this.bloomSourceLayers);
      // Set an explicit MRT so renderer.getMRT() is non-null during this pass.
      // Without it, materials with material.mrtNode (e.g. particles) fall into the
      // "else if (materialMRT !== null)" branch and become the sole fragment output —
      // the normal slot is not in the bloom render target (index -1 → empty struct →
      // invalid shader). With an explicit MRT({ output }) the merge path runs instead
      // and the unknown normal slot is silently ignored.
      bloomSourcePass.setMRT(mrt({ output }));
      bloomInputNode = bloomSourcePass.getTextureNode('output');
      bloomDepthNode = bloomSourcePass.getTextureNode('depth');
    }

    const getMRTTexture = (name: string): any => {
      if (!mrtOutputs.has(name)) return null;
      return scenePass.getTextureNode(name);
    };

    const composeContext: WebGPUComposeContext = {
      scenePass,
      depthNode,
      outputNode,
      camera: this.camera,
      renderer: this.renderer,
      getMRTTexture,
      prePass,
      exclusionMaskTexture,
      bloomInputNode,
      bloomDepthNode,
    };

    // Apply scene context modifications (e.g., builtinAOContext for AO)
    for (const effect of enabledEffects) {
      const effectConfig = this.effectConfigs.get(effect.type);
      if (effect.applySceneContext && effectConfig) {
        effect.applySceneContext(scenePass, effectConfig, composeContext);
      }
    }

    // Compose all effects into the output chain
    for (const effect of enabledEffects) {
      const effectNode = this.effectNodes.get(effect.type);
      if (!effectNode?.compose) continue;

      // Update context with current output
      composeContext.outputNode = outputNode;

      // Compose this effect
      outputNode = effectNode.compose(outputNode, composeContext);
    }

    // Composite HUD on top if available
    if (this.hudScene && this.hudCamera) {
      const hudPass = pass(this.hudScene, this.hudCamera);
      outputNode = blendColor(outputNode, hudPass);
    }

    // Set final output
    this.renderPipeline.outputNode = outputNode;

    // If ToneMapping effect is disabled, ensure renderer-level tone mapping is active.
    // Otherwise the renderer can remain stuck in NoToneMapping from previous effect config.
    const toneMappingConfig = this.effectConfigs.get(PostProcessPass.ToneMapping);
    if (!toneMappingConfig?.enabled) {
      this.applyRendererToneMapping();
    } else {
      this.renderer.toneMapping = THREE.NoToneMapping;
    }

    this.needsRebuild = false;
    this.hooks.onPipelineRebuild.invoke();
  }

  /** Use direct renderer path when only basic tone mapping is active. */
  protected shouldUseDirectRendererFastPath(): boolean {
    if (!this.enabled || this.disposed || !this.initialized) return false;
    if (!this.scene || !this.camera) return false;
    if (this.hiddenObjects.size > 0 || this.excludedObjects.size > 0) return false;
    if (this.hudScene || this.hudCamera) return false;

    const enabledEffects: string[] = [];
    for (const [type, config] of this.effectConfigs) {
      if (config.enabled) enabledEffects.push(type);
    }
    if (enabledEffects.length !== 1 || enabledEffects[0] !== PostProcessPass.ToneMapping) {
      return false;
    }

    const toneConfig = this.effectConfigs.get(PostProcessPass.ToneMapping) as ToneMappingEffectConfig | undefined;
    if (!toneConfig?.enabled) return false;
    if (toneConfig.skipNonToneMappedMaterials) return false;

    return true;
  }

}
